// Persistence for the multi-tab tree state. Tabs hold per-emplacement state
// (active container/folder, which folders are expanded, and — since the shape
// grew — the open location + its back/forward history) so reopening the pane
// drops the user back exactly where they were. Stored in localStorage under a
// versioned key so we can bump cleanly when the shape evolves.

const STORAGE_KEY = 'prism-tabs-v1';

// One visited folder-detail location in a tab's navigation history.
export interface NavEntry {
  containerId: string | null;
  mode: 'tree' | 'detail';
  detailFolderId: string | null;
}

export interface PersistedTab {
  id: string;
  containerId: string | null;
  folderId: string | null;
  expanded: string[];
  // Added after the initial v1 shape: the open location + browser-style
  // back/forward history, so a reopen restores the exact page and its history.
  // Optional so tabs written by the older shape still load (defaults on hydrate).
  mode?: 'tree' | 'detail';
  detailFolderId?: string | null;
  history?: NavEntry[];
  historyIndex?: number;
}

export interface PersistedState {
  tabs: PersistedTab[];
  activeTabId: string;
}

export function loadPersisted(): PersistedState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !Array.isArray((parsed as PersistedState).tabs) ||
      typeof (parsed as PersistedState).activeTabId !== 'string'
    ) {
      return null;
    }
    const state = parsed as PersistedState;
    if (state.tabs.length === 0) return null;
    if (!state.tabs.some((t) => t.id === state.activeTabId)) {
      state.activeTabId = state.tabs[0]!.id;
    }
    return state;
  } catch {
    return null;
  }
}

export function savePersisted(state: PersistedState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage may be full / disabled — silent ignore, state is not critical.
  }
}
